import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Activity, Target, Goal, Clock } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const empty = {
  player_id: "", match_date: new Date().toISOString().slice(0, 10),
  opponent: "", competition: "",
  minutes_played: "90", goals: "0", assists: "0",
  yellow_cards: "0", red_cards: "0", rating: "",
};

export default function Performances() {
  const [list, setList] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState("all");
  const [agg, setAgg] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => {
    const q = selected !== "all" ? { params: { player_id: selected } } : {};
    api.get("/performances", q).then(({ data }) => setList(data));
    if (selected !== "all") {
      api.get(`/performances/aggregate/${selected}`).then(({ data }) => setAgg(data));
    } else {
      setAgg(null);
    }
  };
  useEffect(() => { load(); }, [selected]);
  useEffect(() => { api.get("/players").then(({ data }) => setPlayers(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      minutes_played: parseInt(form.minutes_played, 10),
      goals: parseInt(form.goals, 10),
      assists: parseInt(form.assists, 10),
      yellow_cards: parseInt(form.yellow_cards, 10),
      red_cards: parseInt(form.red_cards, 10),
      rating: form.rating ? parseFloat(form.rating) : null,
    };
    try {
      await api.post("/performances", payload);
      toast.success("Performance enregistrée");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error("Erreur"); }
  };

  const onDel = async (id) => {
    if (!confirm("Supprimer ?")) return;
    await api.delete(`/performances/${id}`);
    load();
  };

  const chartData = list.slice(0, 10).reverse().map(p => ({
    name: (p.match_date || "").slice(5),
    goals: p.goals, assists: p.assists,
  }));

  return (
    <div>
      <PageHeader
        overline="Statistiques"
        title="Performances joueurs"
        description="Buts, passes, minutes, notes par match."
        actions={
          <Button onClick={() => setOpen(true)} data-testid="create-perf-btn"
            className="btn-club">
            <Plus className="w-4 h-4 mr-2" /> Saisir performance
          </Button>
        }
      />

      <div className="bg-white border border-[#E0E0E0] rounded-md p-4 mb-4 flex gap-3 items-center">
        <Label className="shrink-0">Joueur :</Label>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="max-w-xs" data-testid="perf-player-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les joueurs</SelectItem>
            {players.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {agg && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="perf-aggregate">
          <Stat icon={Activity} label="Matchs" value={agg.matches} color="#1976D2" />
          <Stat icon={Goal} label="Buts" value={agg.goals} color="#C62828" />
          <Stat icon={Target} label="Passes décisives" value={agg.assists} color="#2E7D32" />
          <Stat icon={Clock} label="Minutes jouées" value={agg.minutes} color="#7B1FA2" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E0E0E0] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="performances-table">
              <thead className="bg-[#F4F5F7] text-[#616161] tracking-overline text-[10px]">
                <tr>
                  <th className="text-left px-3 py-3">Date</th>
                  <th className="text-left px-3 py-3">Joueur</th>
                  <th className="text-left px-3 py-3">Adversaire</th>
                  <th className="text-right px-3 py-3">Min.</th>
                  <th className="text-right px-3 py-3">Buts</th>
                  <th className="text-right px-3 py-3">P.D.</th>
                  <th className="text-right px-3 py-3">Note</th>
                  <th className="text-right px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E0E0E0]">
                {list.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-[#616161]">Aucune performance.</td></tr>
                ) : list.map(p => (
                  <tr key={p.id} className="hover:bg-[#F4F5F7]/50">
                    <td className="px-3 py-3 font-mono text-xs">{p.match_date}</td>
                    <td className="px-3 py-3 font-medium">{p.player_name}</td>
                    <td className="px-3 py-3 text-[#616161]">{p.opponent || "—"}</td>
                    <td className="px-3 py-3 text-right">{p.minutes_played}</td>
                    <td className="px-3 py-3 text-right font-semibold text-[#C62828]">{p.goals}</td>
                    <td className="px-3 py-3 text-right font-semibold text-[#2E7D32]">{p.assists}</td>
                    <td className="px-3 py-3 text-right">{p.rating || "—"}</td>
                    <td className="px-3 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => onDel(p.id)}><Trash2 className="w-4 h-4 text-[#C62828]" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-[#E0E0E0] rounded-md p-5">
          <h3 className="font-display text-lg font-bold mb-3">10 derniers matchs</h3>
          {chartData.length === 0 ? (
            <p className="text-sm text-[#616161]">Pas de données.</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis dataKey="name" fontSize={10} />
                  <YAxis fontSize={10} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="goals" fill="#C62828" name="Buts" />
                  <Bar dataKey="assists" fill="#2E7D32" name="Passes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="perf-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouvelle performance</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Joueur *</Label>
              <Select value={form.player_id} onValueChange={(v) => setForm({ ...form, player_id: v })}>
                <SelectTrigger data-testid="perf-player"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {players.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date *</Label><Input type="date" required value={form.match_date} onChange={(e) => setForm({ ...form, match_date: e.target.value })} /></div>
              <div><Label>Adversaire</Label><Input value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} /></div>
              <div className="col-span-2"><Label>Compétition</Label><Input value={form.competition} onChange={(e) => setForm({ ...form, competition: e.target.value })} placeholder="Ligue 1 BJ" /></div>
              <div><Label>Minutes</Label><Input type="number" value={form.minutes_played} onChange={(e) => setForm({ ...form, minutes_played: e.target.value })} /></div>
              <div><Label>Buts</Label><Input type="number" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} data-testid="perf-goals" /></div>
              <div><Label>Passes décisives</Label><Input type="number" value={form.assists} onChange={(e) => setForm({ ...form, assists: e.target.value })} /></div>
              <div><Label>Note /10</Label><Input type="number" step="0.1" min="0" max="10" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
              <div><Label>Cartons jaunes</Label><Input type="number" value={form.yellow_cards} onChange={(e) => setForm({ ...form, yellow_cards: e.target.value })} /></div>
              <div><Label>Cartons rouges</Label><Input type="number" value={form.red_cards} onChange={(e) => setForm({ ...form, red_cards: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="perf-submit-btn">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E0E0E0] rounded-md p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs tracking-overline text-[#616161]">{label}</p>
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="font-display text-2xl font-extrabold tracking-tight">{value}</p>
    </div>
  );
}
